/**
 * Data tab for an `INPUT_BOX` - "Fill from data": the box starts with one
 * column of a dataset, summarised and filtered by the dashboard's dropdowns,
 * instead of a fixed default. The user can still type over it; a filter change
 * brings the new value in. Stored on `widget.binding` as `valueDataset` /
 * `valueField` / `valueAggregation` (see `design-input-data.utils`).
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../../shared/ss-select-tooltip.directive';

import type { DesignNode } from '../../design-builder.types';
import { DesignDataService } from '../../services/design-data.service';
import { DesignRuntimeService } from '../../data/design-runtime.service';
import { inputDataBindingOf } from '../../utils/design-input-data.utils';
import type { InputSaveConfig } from '../../utils/design-save-request.utils';

@Component({
  selector: 'app-design-input-box-data',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, NgSelectModule, SsSelectTooltipDirective],
  template: `
    <div class="ddp-head"><strong>Fill from data</strong></div>
    <p class="ddp-hint">
      The box shows this column's value for the current dropdown selection. Users can still type over it;
      changing a dropdown brings the new value in.
    </p>

    <div class="ddp-field">
      <label>Dataset</label>
      <ng-select #ibDataset class="ss-select" [class.is-open]="!!ibDataset.isOpen()" appendTo="body"
        [items]="datasetOptions()" [searchable]="true" [clearable]="true"
        [loading]="data.datasets().status === 'loading'" placeholder="Select a dataset"
        [ngModel]="raw()['valueDataset'] || null" (ngModelChange)="patch({ valueDataset: $event || '', valueField: '' })"></ng-select>
    </div>

    <div class="ddp-field">
      <label>Column</label>
      <ng-select #ibColumn class="ss-select" [class.is-open]="!!ibColumn.isOpen()" appendTo="body"
        [items]="columnOptions()" [searchable]="true" [clearable]="true"
        [disabled]="!raw()['valueDataset']" [loading]="columnState()?.status === 'loading'"
        [placeholder]="raw()['valueDataset'] ? 'Select a column' : 'Select a dataset first'"
        [ngModel]="raw()['valueField'] || null" (ngModelChange)="patch({ valueField: $event || '' })"></ng-select>
    </div>

    <div class="ddp-field">
      <label>Summary</label>
      <ng-select #ibAgg class="ss-select" [class.is-open]="!!ibAgg.isOpen()" appendTo="body"
        [items]="data.aggregations()" bindLabel="label" bindValue="value" [clearable]="false" [searchable]="false"
        [disabled]="!raw()['valueField']"
        [ngModel]="raw()['valueAggregation'] || 'SUM'" (ngModelChange)="patch({ valueAggregation: $event })"></ng-select>
      <p class="ddp-status">One row selected (e.g. a spec): SUM, MAX or MIN all give that row's value.</p>
    </div>

    <p class="ddp-status" [class.is-error]="preview().error" *ngIf="preview().text">{{ preview().text }}</p>
    <button type="button" class="ddp-link" *ngIf="binding()" (click)="clear()">Remove data link</button>

    <!-- Save (update): as a nested table row's Save - the same request body. -->
    <div class="ddp-head ib-save-head"><strong>Save to API</strong></div>
    <label class="ddp-check">
      <input type="checkbox" [checked]="save().enabled === true" (change)="patchSave({ enabled: $any($event.target).checked })" />
      Save button (update)
    </label>
    <ng-container *ngIf="save().enabled === true">
      <p class="ddp-hint">
        A Save button shows beside the box. It sends the box's value in its column, with each business key's value
        taken from the dashboard's current selection (dropdowns, the screen it was opened from).
      </p>
      <div class="ddp-field">
        <label>Button name</label>
        <input type="text" class="ddp-input" [value]="save().label ?? ''" (change)="patchSave({ label: $any($event.target).value })" placeholder="Save" />
      </div>
      <div class="ddp-field">
        <label>Save API URL</label>
        <input type="text" class="ddp-input" [value]="save().url ?? ''" (change)="patchSave({ url: $any($event.target).value })" placeholder="/api/…" />
      </div>
      <div class="ddp-field">
        <label>Entity</label>
        <ng-select #ibSaveEntity class="ss-select" [class.is-open]="!!ibSaveEntity.isOpen()" appendTo="body"
          [items]="datasetOptions()" [addTag]="true" [searchable]="true" [clearable]="true"
          [placeholder]="raw()['valueDataset'] ? 'Same as the dataset (' + raw()['valueDataset'] + ')' : 'Select an entity'"
          [ngModel]="save().entity || null" (ngModelChange)="patchSave({ entity: $event || '' })"></ng-select>
      </div>
      <div class="ddp-field">
        <label>Business keys</label>
        <ng-select #ibSaveKeys class="ss-select" [class.is-open]="!!ibSaveKeys.isOpen()" appendTo="body"
          [items]="saveColumnOptions()" [multiple]="true" [addTag]="true" [searchable]="true" [clearable]="true"
          [loading]="saveColumnState()?.status === 'loading'" placeholder="Select the business keys"
          [ngModel]="save().businessKeys ?? []" (ngModelChange)="patchSave({ businessKeys: $event || [] })"></ng-select>
        <p class="ddp-status">Each key is sent with the value the dashboard is on for that column (e.g. cust_spec_no picked in a dropdown).</p>
      </div>
      <div class="ddp-field">
        <label>Column to save</label>
        <ng-select #ibSaveColumn class="ss-select" [class.is-open]="!!ibSaveColumn.isOpen()" appendTo="body"
          [items]="saveColumnOptions()" [addTag]="true" [searchable]="true" [clearable]="true"
          [loading]="saveColumnState()?.status === 'loading'"
          [placeholder]="raw()['valueField'] ? 'Same as the column above (' + raw()['valueField'] + ')' : 'Select a column'"
          [ngModel]="save().column || null" (ngModelChange)="patchSave({ column: $event || '' })"></ng-select>
      </div>
    </ng-container>
  `,
  styles: [`
    :host { display: block; margin-bottom: 16px; }
    .ib-save-head { margin-top: 16px; }
    .ddp-input { width: 100%; box-sizing: border-box; height: 34px; padding: 0 10px; border: 1px solid #d0d5dd; border-radius: 8px; font: inherit; }
  `],
})
export class InputBoxDataComponent {
  protected readonly data = inject(DesignDataService);
  private readonly runtime = inject(DesignRuntimeService);

  readonly node = input.required<DesignNode>();
  /** The full binding to store on the widget. */
  readonly bindingChange = output<Record<string, unknown>>();

  readonly raw = computed(() => (this.node().widget?.binding ?? {}) as Record<string, unknown>);
  readonly binding = computed(() => inputDataBindingOf(this.node()));

  readonly datasetOptions = computed(() => {
    const state = this.data.datasets();
    return state.status === 'ready' ? state.value : [];
  });

  readonly columnState = computed(() => {
    const dataset = String(this.raw()['valueDataset'] ?? '');
    return dataset ? this.data.columns(dataset) : null;
  });
  readonly columnOptions = computed(() => {
    const state = this.columnState();
    return state?.status === 'ready' ? state.value : [];
  });

  readonly preview = computed<{ text: string; error: boolean }>(() => {
    if (!this.binding()) return { text: '', error: false };
    const state = this.runtime.inputDataValues()[this.node().id];
    if (!state || state.status === 'loading') return { text: 'Loading value…', error: false };
    if (state.status === 'error') return { text: state.message, error: true };
    return { text: `Value now: ${state.value ?? '(empty)'}`, error: false };
  });

  /** The "Save button (update)" settings (binding.save). */
  readonly save = computed<InputSaveConfig>(() => {
    const raw = this.raw()['save'];
    return raw && typeof raw === 'object' ? (raw as InputSaveConfig) : {};
  });

  /** The entity the Save updates: its own, else the Fill-from-data dataset. */
  private readonly saveEntity = computed(() => this.save().entity?.trim() || String(this.raw()['valueDataset'] ?? ''));
  readonly saveColumnState = computed(() => {
    const entity = this.saveEntity();
    return entity ? this.data.columns(entity) : null;
  });
  readonly saveColumnOptions = computed(() => {
    const state = this.saveColumnState();
    return state?.status === 'ready' ? state.value : [];
  });

  constructor() {
    this.data.loadDatasets();
    // Columns load on demand: fetch the picked dataset's list.
    effect(() => {
      const dataset = String(this.raw()['valueDataset'] ?? '');
      if (dataset) untracked(() => this.data.loadColumns(dataset));
    });
    effect(() => {
      const entity = this.saveEntity();
      if (entity) untracked(() => this.data.loadColumns(entity));
    });
  }

  patchSave(change: Partial<InputSaveConfig>): void {
    this.bindingChange.emit({ ...this.raw(), save: { ...this.save(), ...change } });
  }

  patch(change: Record<string, unknown>): void {
    const next: Record<string, unknown> = { ...this.raw(), ...change };
    if (!next['valueAggregation']) next['valueAggregation'] = 'SUM';
    this.bindingChange.emit(next);
    // The store is updated by now: fetch the value with the new link.
    if (next['valueDataset'] && next['valueField']) this.runtime.connectInputData(this.node().id);
  }

  clear(): void {
    const { valueDataset: _d, valueField: _f, valueAggregation: _a, ...rest } = this.raw();
    this.bindingChange.emit(rest);
  }
}
